import type { ReactElement, ReactNode, SyntheticEvent } from "react"
import { useState } from "react"
import type { ButtonAtmProps } from "@onestore/hel/dist/components/atoms/ButtonAtm"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import _t from "@core/i18n"
import useFastCheckoutAction from "@gatsby-plugin-basket/hooks/useFastCheckoutAction"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import { ChangePeriodModal } from "@gatsby-plugin-generic-page/components/ProductBoxSlim/ChangePeriodModal"
import type { ChangePeriodModal as ChangePeriodModalType } from "@gatsby-plugin-generic-page/fragments/changePeriodModal"
import getAxDrawerOpenArgs from "@gatsby-plugin-generic-page/helpers/getAxDrawerOpenArgs"
import { getCloudBluePlanForPeriod } from "@gatsby-plugin-generic-page/helpers/getCloudBluePlanForPeriod"
import getSaleConfigurationByPlanInputValue from "@gatsby-plugin-generic-page/helpers/getSaleConfigurationByPlanInputValue"
import { getUpsellData } from "@gatsby-plugin-generic-page/helpers/getUpsellData"
import { isPlanInputCounterBox } from "@gatsby-plugin-generic-page/helpers/isPlanInputCounterBox"
import { useLoadedProductBoxTitleMarkdown } from "@gatsby-plugin-generic-page/hooks/useProductBoxTitleWithLabels"
import {
  CardTariff,
  Divider,
  Price,
  Text,
} from "@ionos-web-design-system/react"
import get from "lodash/get"
import { useIsCpShop } from "~/context/CpShopContext"
import type { ProductBoxBundle } from "~/fragments/bundle"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import type { ProductBoxSlimHookProps } from "~/hooks/useProductBoxSlim"
import useProductBoxSlim from "~/hooks/useProductBoxSlim"
import isEmpty from "~/lib/isEmpty"
import { showPlanOverLimitModal } from "~/store/basket/actions"
import { getPlansUsageOverLimit } from "~/store/basket/selectors"
import { showAxDrawer } from "~/store/bonus/actions-ax-drawer"
import { useAppDispatch, useAppSelector } from "~/store/hooks"
import type { AppState } from "~/store/reducer"
import { ConfigDrawer } from "../../../../../src/components/ConfigDrawer/ConfigDrawer"
import type { ProductType } from "."
import ProductBoxButton from "./ProductBoxButton"
import TrialProductBox from "./TrialProductBox"

export type ProductBoxWithSaleConfigurationProps = {
  product: ProductType
  saleConfiguration: SaleConfiguration[]
  bundle?: ProductBoxBundle[]
  planInputLabel?: string
  changePeriodModal?: ChangePeriodModalType["flatData"]
  index?: number
  isTrialBox?: boolean
  hasAnyOuterFooter?: boolean
  hasUpsellDrawer?: boolean
}

type ButtonProps = Omit<ButtonAtmProps, "size" | "isWider" | "isExpanded">

// CardTariff always reserves 8 subgrid rows regardless of promoText — when
// there's no badge it renders an empty spacer row in its place — so the
// outer wrapper's row budget for the card itself must always be 8, not
// conditional on promoText, or the outer footer collides with the card.
// Written as literal Tailwind classes below (not an interpolated arbitrary
// value) because Tailwind only generates CSS for class names it can find
// as static text in the source.
const CARD_ROW_SPAN_CLASS = "row-span-[8]"
const CARD_ROW_SPAN_WITH_FOOTER_CLASS = "row-span-[9]"

export default function ProductBoxWithSaleConfiguration({
  product,
  saleConfiguration,
  bundle,
  planInputLabel,
  changePeriodModal,
  index,
  isTrialBox,
  hasAnyOuterFooter,
  hasUpsellDrawer,
}: ProductBoxWithSaleConfigurationProps): ReactElement<ProductBoxWithSaleConfigurationProps> | null {
  const {
    defaultCloudBluePlan,
    bundlePlan,
    upsell,
    optionsMapping,
    skipBonusScreen,
    configDrawerPeriods,
  } = saleConfiguration[0].flatData
  const dispatch = useAppDispatch()
  const plansOverLimit = useAppSelector(getPlansUsageOverLimit)
  const isCpShop = useIsCpShop()

  let plan: ProductBoxSlimHookProps["plan"] = defaultCloudBluePlan[0]?.flatData
  let resolvedSaleConfiguration = saleConfiguration
  let isPeriodUnavailable = false

  const dataTestId =
    index !== null && index !== undefined
      ? `product_box_${index}_select_button`
      : "product_box_select_button"

  if (isPlanInputCounterBox(saleConfiguration, planInputLabel)) {
    const saleConfigurationWithPlanInputValue = saleConfiguration.filter(
      (configuration) => configuration.flatData.planInputValue
    )

    plan =
      saleConfigurationWithPlanInputValue.length > 1
        ? saleConfigurationWithPlanInputValue.reduce<
            Record<number, CloudBluePlan["flatData"]>
          >((obj, item) => {
            const { planInputValue, defaultCloudBluePlan } = item.flatData

            obj[planInputValue!] = defaultCloudBluePlan[0].flatData

            return obj
          }, {})
        : plan
  } else if (product.period && !isEmpty(optionsMapping)) {
    // Konfiguracja AX (SaleConfiguration.optionsMapping, Squidex label
    // "Opcje konfiguracji produktu") pairs a period with a CloudBluePlan —
    // the shared, section-wide period switcher can select a period the
    // default plan doesn't offer but one of the mapped plans does. Mirrors
    // marketplace's plan+period resolution (getSelectedPlanSettings), but
    // matches directly on each entry's own `period` field rather than a
    // config.controls-driven options key. Boxes without any AX
    // Configuration optionsMapping are untouched here and keep their
    // existing single-plan fallback-to-default behavior.
    const resolvedPlan = getCloudBluePlanForPeriod(
      defaultCloudBluePlan,
      optionsMapping,
      product.period
    )

    if (!resolvedPlan) {
      isPeriodUnavailable = true
    } else if (
      resolvedPlan.flatData.id !== defaultCloudBluePlan[0]?.flatData.id
    ) {
      plan = resolvedPlan.flatData
      resolvedSaleConfiguration = [
        {
          ...saleConfiguration[0],
          flatData: {
            ...saleConfiguration[0].flatData,
            defaultCloudBluePlan: [resolvedPlan],
          },
        },
        ...saleConfiguration.slice(1),
      ]
    }
  }

  const newProduct: ProductBoxSlimHookProps = {
    ...product,
    plan,
    upsell: getUpsellData(upsell),
    planInputLabel,
    index,
    saleConfiguration: resolvedSaleConfiguration,
    bundlePlan: bundle,
    changePeriod: changePeriodModal?.planPeriod.period,
    isTrialBox,
  }

  if (product.period) {
    newProduct["period"] = product.period
  }

  if (bundle?.length && !isEmpty(bundle[0].flatData.plan)) {
    newProduct["bundle"] = [
      defaultCloudBluePlan[0].flatData,
      bundle[0].flatData.plan[0].flatData,
    ]

    if (!isEmpty(bundlePlan)) {
      newProduct["bundle"] = [bundlePlan[0].flatData]
      newProduct["hasChangedDefaultPlan"] = true
    }

    newProduct["isBundleDefaultChecked"] = bundle[0].flatData.checkbox

    if (bundle[0].flatData.title) {
      newProduct["bundledTitle"] = bundle[0].flatData.title
    }
  }

  const productBox = useProductBoxSlim(newProduct)
  const {
    selectedPlan,
    button,
    onChangePeriodModalClick,
    priceData,
    firstPurchaseFreePriceData,
    ...box
  } = productBox

  const titleWithLabels = useLoadedProductBoxTitleMarkdown(box.title ?? "")

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isModalLoading, setIsModalLoading] = useState(false)
  const [isConfigDrawerModalOpen, setIsConfigDrawerModalOpen] = useState(false)
  const isLarge = useBreakpoint("large")
  const isInBasket = useAppSelector((state: AppState) =>
    state.basket.items.some((item) => item.alias === selectedPlan.alias)
  )
  const showChangePeriodModal =
    !isEmpty(changePeriodModal) && isLarge && !isInBasket

  // Uses the period-resolved plan (resolvedSaleConfiguration), not the raw
  // defaultCloudBluePlan — otherwise a skipBonusScreen box whose selected
  // period only a mapped optionsMapping plan offers would add the wrong
  // plan/price to the basket while the card visibly shows the resolved one.
  const { addProductToBasket } = useFastCheckoutAction(
    resolvedSaleConfiguration[0].flatData.defaultCloudBluePlan[0] ??
      defaultCloudBluePlan[0]
  )

  const modalButton: ButtonProps = {
    ...button,
    onClick: showChangePeriodModal
      ? () => setIsModalOpen(true)
      : button.onClick,
    dataTestId: dataTestId,
  }

  const buttonText = product.buttonText || button.text

  const basketButton: ButtonProps = {
    ...button,
    text: buttonText,
    onClick: () => addProductToBasket(),
    dataTestId: dataTestId,
  }

  const hasConfigDrawer = !isEmpty(configDrawerPeriods)

  const configDrawerButton: ButtonProps | null = hasConfigDrawer
    ? {
        ...button,
        text: buttonText,
        onClick: () => {
          setIsConfigDrawerModalOpen(true)
        },
        dataTestId: dataTestId,
      }
    : null

  // Uses the period-resolved plan (resolvedSaleConfiguration), not the raw
  // saleConfiguration — otherwise the drawer would show the box's default
  // (monthly) plan even when a period-mapped plan (e.g. yearly) is selected.
  const selectedSaleConfiguration = getSaleConfigurationByPlanInputValue(
    resolvedSaleConfiguration,
    box.selectedPlanInputValue
  )

  // AxUpsellDrawer always derives its main plan from the sale configuration it
  // is handed, so a swap in effect has to reach it as an already-replaced
  // defaultCloudBluePlan — see getAxDrawerOpenArgs for the swap rules. Reads
  // bundlePlan off selectedSaleConfiguration, not the outer, always-[0]
  // `bundlePlan`, so a plan-input-counter box swaps against the step that's
  // actually selected.
  const axDrawerOpenArgs = getAxDrawerOpenArgs(
    bundle,
    selectedSaleConfiguration.flatData.bundlePlan,
    box.isBundleChecked ?? false
  )

  const saleConfigurationForDrawer = axDrawerOpenArgs.mainPlanOverride
    ? {
        ...selectedSaleConfiguration,
        flatData: {
          ...selectedSaleConfiguration.flatData,
          defaultCloudBluePlan: axDrawerOpenArgs.mainPlanOverride,
        },
      }
    : selectedSaleConfiguration

  // Forces the cp-shop-style AX upsell drawer instead of the normal
  // basket/modal/config-drawer path — the section this box belongs to
  // opted into it via hasUpsellDrawer. AxUpsellDrawer is only mounted
  // inside cp-shop's ShopPage, so this is gated on isCpShop — elsewhere
  // it falls through to configDrawerButton/basketButton/modalButton
  // instead of silently dispatching to a drawer nothing is listening to.
  const axDrawerButton: ButtonProps | null =
    hasUpsellDrawer && isCpShop
      ? {
          ...button,
          text: buttonText,
          onClick: () =>
            dispatch(
              showAxDrawer(
                saleConfigurationForDrawer,
                // Always carry the bundle in the "add alongside" case, so the
                // drawer can show its row and let the bundle be opted into from
                // there. The box's own checkbox only decides the *initial*
                // checked state. A swap-configured box has no second product to
                // add — only a different main plan — so it passes no bundle at
                // all, checked or not.
                axDrawerOpenArgs.bundle,
                axDrawerOpenArgs.isBundleSelected
              )
            ),
          dataTestId: dataTestId,
        }
      : null

  const onChangePeriodModalConfirmed = () => {
    setIsModalLoading(true)

    if (onChangePeriodModalClick) onChangePeriodModalClick()
  }

  const onChangePeriodModalClose = (event: SyntheticEvent) => {
    setIsModalLoading(true)

    if (button.onClick) button.onClick(event)
  }

  const isPlanOverLimit = plansOverLimit.includes(selectedPlan.id)
  const planOverLimitModalButton: ButtonProps | null = isPlanOverLimit
    ? {
        ...button,
        text: buttonText,
        onClick: () => dispatch(showPlanOverLimitModal()),
        dataTestId: dataTestId,
      }
    : null

  const activeButtonProps =
    planOverLimitModalButton ||
    axDrawerButton ||
    configDrawerButton ||
    (skipBonusScreen ? basketButton : modalButton)

  const buttonElement: ReactNode = (
    <ProductBoxButton
      concept={product.concept}
      variant={product.variant}
      onClick={activeButtonProps.onClick}
      disabled={activeButtonProps.isDisabled}
      dataTestId={activeButtonProps.dataTestId}
    >
      {activeButtonProps.text}
    </ProductBoxButton>
  )

  const firstPurchaseFreePriceElement = !isEmpty(firstPurchaseFreePriceData) ? (
    <>
      <Price data={firstPurchaseFreePriceData} className="mb-4 mt-6" />

      <div className="flex items-center gap-gap-4">
        <Divider orientation="horizontal" className="flex-1" />

        <Text variant="bodyXs" className="shrink-0">
          {_t("packageBox.later")}
        </Text>

        <Divider orientation="horizontal" className="flex-1" />
      </div>
    </>
  ) : null

  const cardContent = (
    <CardTariff
      title={titleWithLabels}
      underTitle={box.bundleText}
      subtitle={box.description}
      badgeText={box.promoText}
      subHeader={firstPurchaseFreePriceElement}
      priceData={
        !isEmpty(priceData)
          ? { ...priceData, reservePrelineSpace: true }
          : undefined
      }
      button={buttonElement}
      className="leading-normal"
    >
      <div className="flex flex-col gap-gap-8 h-full">
        {box.parameters}

        {box.footer || box.additionalButton ? (
          // Pins the footer/upsell box to the bottom of the card instead of
          // letting its position drift with the (variable-length) parameter
          // list above it, so it lands on the same line across sibling cards.
          <div className="flex flex-col gap-gap-8 mt-auto">
            {box.footer ?? null}

            {box.additionalButton ?? null}
          </div>
        ) : null}
      </div>
    </CardTariff>
  )

  if (isPeriodUnavailable) {
    return null
  }

  return (
    <>
      <div
        // Sibling cards in the same ProductBoxesGrid row must all reserve
        // the same number of subgrid rows, or their shared row lines drift
        // apart and footers/upsells no longer land on the same line — so
        // this reserves the extra footer row whenever ANY sibling has one,
        // not just when this particular box does.
        className={`grid grid-rows-subgrid ${hasAnyOuterFooter ? CARD_ROW_SPAN_WITH_FOOTER_CLASS : CARD_ROW_SPAN_CLASS}`}
      >
        {isTrialBox &&
        !isEmpty(box.backgroundImage) &&
        !isEmpty(box.customTextColor) ? (
          <TrialProductBox
            title={titleWithLabels}
            underTitle={box.bundleText}
            subtitle={box.description}
            badgeText={box.promoText}
            button={buttonElement}
            imageBackground={box.backgroundImage}
            customOnColorText={box.customTextColor}
          >
            {product.content.parameters}
          </TrialProductBox>
        ) : (
          cardContent
        )}

        {box.outerFooter ?? null}
      </div>

      {showChangePeriodModal ? (
        <ChangePeriodModal
          isModalOpen={isModalOpen}
          isModalLoading={isModalLoading}
          onConfirmedAction={onChangePeriodModalConfirmed}
          onClose={onChangePeriodModalClose}
          {...changePeriodModal}
        />
      ) : null}

      {hasConfigDrawer &&
      isConfigDrawerModalOpen &&
      !isEmpty(saleConfiguration) ? (
        <ConfigDrawer
          configPeriods={configDrawerPeriods}
          saleConfiguration={resolvedSaleConfiguration[0]}
          bundleData={
            !isEmpty(bundle)
              ? {
                  bundlePlan: !isEmpty(bundlePlan)
                    ? bundlePlan[0]
                    : get(bundle[0], "flatData.plan[0]"),
                  isDefaultChecked: box.isBundleChecked,
                  hasChangedDefaultPlan:
                    newProduct["hasChangedDefaultPlan"] || false,
                  bundleTitleSuffix: newProduct["bundledTitle"],
                  bundleProductName: bundle[0].flatData.text,
                  bundleTooltip: get(
                    bundle[0],
                    "flatData.tooltip[0].flatData.text"
                  ),
                  bundleValue: bundle[0].flatData.value,
                }
              : undefined
          }
          onClose={() => setIsConfigDrawerModalOpen(false)}
        />
      ) : null}
    </>
  )
}
